import { Inject } from '@odinjs/odin';

import { Endpoint, ProviderAccreditationPendenciesService, ProviderAccreditationStatusService, Response, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-provider-components';
import { ActionAPI, FetchAPI, FieldAPI, FormAPI, LegendsAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnCancel, OnClick, OnClose, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { FragmentNames } from '../../codegen';
import { AccreditationMedicineMaterialsAliases as Aliases } from '../../codegen/constants/aliases/accreditation-medicine-materials-aliases';
import BaseController from '../BaseController';

import { AccreditationStatus, AccreditationSteps } from './accreditation-update-controller';
import { ActionTypes } from './update-accreditation-type';

interface MedicineMaterials {
  nrSeqProvider: number,
  nrSeqAccreditation: number,
  nrSequence: number,
  ieAction: ActionTypes,
  dsAction: string,
  preLoadedMaterial: string,
}

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
};

@Controller({ fragment: FragmentNames.ACCREDITATION_MEDICINE_MATERIALS })
class AccreditationMedicineMaterials extends BaseController {
  @Inject(Aliases.FETCH_MEDICINE_MATERIALS_LIST)
  private fetchMedicineMaterialsList: FetchAPI;
  @Inject(Aliases.FORM_MEDICINE_MATERIAL)
  private formMedicineMaterial: FormAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;
  @Inject(Aliases.FIELD_MEDICINE_MATERIAL)
  private fieldMedicineMaterial: FieldAPI;
  @Inject(Aliases.LEGENDS)
  private legends: LegendsAPI;
  @Inject(Aliases.PANEL_ADD)
  private panelAdd: PanelAPI;

  @OnMount(Aliases.FETCH_MEDICINE_MATERIAL)
  onBeforeFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const nrSequence = this.context.get('nrSeqMaterial');
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    if (nrSequence) {
      api.setParameters({ nrSeqProvider, nrSeqAccreditation, nrSequence });
      api.forceFetch();
      this.context.set({ 'nrSeqMaterial': undefined });
    }
  }

  @OnAfterFetch(Aliases.FETCH_MEDICINE_MATERIALS_LIST)
  onAfterFetchMedicineMaterialsList(event: FetchEvent): void {
    const { data } = event;
    if (isEmptyArrayOrObject(data)) {
      this.legends.setShowExpression('');
      this.legends.setHideExpression('');
    } else {
      this.legends.setShowExpression(MessageService.translate('tws.legends.show'));
      this.legends.setHideExpression(MessageService.translate('tws.legends.hide'));
    }
  }

  @OnMount(Aliases.FIELD_VALUE)
  onMountFieldValue(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const { preLoadedMaterial } = api.getData();
    api.setVisible(preLoadedMaterial == 'N');
  }

  @HPMSOnOptionClick(Aliases.CARD_MEDICINE_MATERIALS)
  onOptionClick(event: HpmsCustomCardEvent<MedicineMaterials>): void {

    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams() as unknown as MedicineMaterials;
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const { data, key } = event;
    const { nrSequence } = data;

    const action = {
      [OptionKey.DELETE]: () => this.deleteRecord(nrSeqProvider, nrSeqAccreditation, nrSequence),
      [OptionKey.EDIT]: () => this.editRecord(nrSequence),
    };

    if (action[key]) {
      action[key]();
    }
  }

  @OnMount(Aliases.ACTION_ADD)
  onMountActionAdd(event: MountEvent): void {
    const api = event.target as ActionAPI;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;

    ProviderAccreditationStatusService.getAccreditationStatus(Number(nrSeqAccreditation), Number(nrSeqProvider))
      .then((response) => {
        const { status } = response.body as unknown as Response;
        api.setEnabled(status == AccreditationStatus.AWAITING_REQUESTER || status == AccreditationStatus.AWAITING_REVISION);
      }).catch((response) => ToastService.open(response));
  }

  @OnClick(Aliases.ACTION_ADD)
  onClickActionAdd(): void {
    this.panelAdd.setVisible(true);
  }

  @OnClose(Aliases.MODAL_MATERIAL)
  onCloseModal(): void {
    this.panelAdd.setVisible(false);
  }

  @HPMSOnCreateMenu(Aliases.CARD_MEDICINE_MATERIALS)
  onCreateMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { editable } = data;
    shouldRender(editable == 'S');
  }

  @HPMSOnCreateOption(Aliases.CARD_MEDICINE_MATERIALS)
  onCreateOptionMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { editable, preLoadedMaterial } = data;
    const show = {
      [OptionKey.EDIT]: editable == 'S' && preLoadedMaterial == 'N',
      [OptionKey.DELETE]: true,
    };
    shouldRender(show[key]);
  }

  private editRecord(nrSequence: number): void {
    this.panelAdd.setVisible(true);
    this.context.set({ nrSeqMaterial: nrSequence });
  }

  private deleteRecord(nrSeqProvider: number, nrSeqAccreditation: number, nrSequence: number): void {
    HttpService.delete(Endpoint.DELETE.MEDICINE_MATERIAL, { nrSeqProvider, nrSequence, nrSeqAccreditation })
      .then(() => {
        this.fetchMedicineMaterialsList.forceFetch();
      }).catch(() => {
        ToastService.open({ message: 'provider.error-process-request', level: ToastLevel.ERROR });
        this.fetchMedicineMaterialsList.forceFetch();
      });
  }

  @OnCancel(Aliases.FORM_MEDICINE_MATERIAL)
  onCleanForm(): void {
    this.cleanForm();
    this.panelAdd.setVisible(false);
  }

  private cleanForm(): void {
    this.fieldMedicineMaterial.setValue(undefined);
    this.formMedicineMaterial.setRecord({});
    this.fieldMedicineMaterial.setState({ showValidations: false });
  }

  @OnSubmit(Aliases.FORM_MEDICINE_MATERIAL)
  onSubmitForm(event: FormAPI): void {
    const body = event.record;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    this.formMedicineMaterial.setLoading(true);
    HttpService.post(Endpoint.POST.MEDICINE_MATERIAL, { nrSeqProvider, nrSeqAccreditation }, { body })
      .then(() => {
        this.panelAdd.setVisible(false);
        this.fetchMedicineMaterialsList.forceFetch();
      }).catch((response) => {
        this.formMedicineMaterial.setLoading(false);
        ToastService.open(response);
      });
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.MEDICINE_MATERIALS);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, event);
    }
  }

}

export {
  AccreditationMedicineMaterials,
};
